import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, X, FileDown, ImageDown, Images } from "lucide-react";
import FileUploadButton from "@/components/admin/FileUploadButton";
import { ScaledPoster } from "@/components/poster/PosterCanvas";
import { exportPosterPdf, exportPosterPng, renderPosterBlob } from "@/lib/posterExport";
import { toImageUrl } from "@/lib/driveUrl";
const EMPTY = {
  title: "",
  title_2: "",
  subtitle: "",
  date: "",
  time: "",
  info: "",
  venue_name: "",
  venue_address: "",
  prices: "",
  contact: "",
  qr_url: "",
  theme_color: "#3F6B3F",
  dark_text: false,
  template: "clair",
  title_size: 100,
  title2_size: 100,
  subtitle_size: 100,
  date_size: 100,
  info_size: 100,
  panel_size: 100,
  contact_size: 100,
  main_image: "",
  secondary_images: [],
  logos: [],
  detour_logos: false,
  published: true,
};

const inputClass = "w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3 text-sm focus:border-[#B83227] focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-bold uppercase text-stone-700";
const pickerBtnClass = "inline-flex items-center gap-1.5 rounded border border-stone-300 px-3 py-2 text-xs font-bold uppercase text-stone-600 transition-colors duration-300 hover:border-stone-500";

/** Champ « coller un lien » : Google Drive (proxy du site) ou Google Photos (résolu en image directe). */
function ImageLinkInput({ onAdd, testId }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const url = value.trim();
    if (!url) return;
    setBusy(true);
    try {
      let finalUrl = url;
      if (url.includes("photos.app.goo.gl") || url.includes("photos.google.com")) {
        const r = await api.get("/resolve-image", { params: { url } });
        finalUrl = r.data.url || url;
      }
      onAdd(finalUrl);
      setValue("");
      toast.success("Image ajoutée depuis le lien");
    } catch {
      toast.error("Impossible de lire ce lien. Vérifiez qu'il s'agit d'un partage public.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex w-full items-center gap-2">
      <input
        data-testid={testId}
        className={inputClass}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submit())}
        placeholder="ou collez un lien Google Drive / Google Photos..."
      />
      <button type="button" onClick={submit} disabled={busy || !value.trim()} data-testid={`${testId}-add`} className="shrink-0 rounded bg-[#1E1B18] px-4 py-2.5 text-xs font-bold uppercase text-white hover:bg-stone-700 disabled:opacity-60">
        {busy ? "..." : "Ajouter"}
      </button>
    </div>
  );
}

/** Sélecteur d'images déjà présentes sur le site : fichiers téléversés + photos de la médiathèque. */
function ImagePicker({ onPick, onClose }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    Promise.all([
      api.get("/admin/files").then((r) => r.data.map((f) => `${base}${f.url}`)).catch(() => []),
      api.get("/media").then((r) => r.data.filter((m) => m.kind === "photo" && m.url).map((m) => m.url)).catch(() => []),
    ]).then(([files, media]) => {
      setImages([...new Set([...files, ...media])]);
      setLoading(false);
    });
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="image-picker-modal">
      <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold uppercase text-[#1E1B18]">Choisir une image du site</h3>
          <button type="button" onClick={onClose} data-testid="image-picker-close" className="rounded border border-stone-300 p-2 text-stone-500 hover:border-stone-500" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>
        {loading ? (
          <p className="py-8 text-center text-sm text-stone-500">Chargement des images...</p>
        ) : images.length === 0 ? (
          <p className="py-8 text-center text-sm text-stone-500">Aucune image sur le site pour le moment — téléversez-en une d'abord.</p>
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {images.map((url, i) => (
              <button key={i} type="button" onClick={() => { onPick(url); onClose(); }} data-testid={`image-picker-item-${i}`} className="overflow-hidden rounded-lg border border-stone-200 transition-transform duration-300 hover:scale-105 hover:border-[#B83227]">
                <img src={toImageUrl(url)} alt="" loading="lazy" className="h-24 w-full object-cover" onError={(e) => (e.currentTarget.parentElement.style.display = "none")} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function safeName(p) {
  return `affiche-${(p.subtitle || p.title || "concert").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "concert"}`;
}

export default function PostersManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");
  const [picker, setPicker] = useState(null); // "main" | "secondary" | "logo" | null

  const load = () => api.get("/admin/posters").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setListItem = (key, i, value) => setForm((f) => ({ ...f, [key]: f[key].map((v, j) => (j === i ? value : v)) }));
  const addListItem = (key, value = "") => setForm((f) => ({ ...f, [key]: [...f[key], value] }));
  const removeListItem = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = editingId
        ? await api.put(`/admin/posters/${editingId}`, form)
        : await api.post("/admin/posters", form);
      // Génère un instantané image de l'affiche : c'est cette image, fiable et légère,
      // qui est affichée sur la page Actualités (la version dynamique reste en secours).
      try {
        const blob = await renderPosterBlob(form);
        const fd = new FormData();
        fd.append("file", new File([blob], `affiche-${Date.now()}.jpg`, { type: "image/jpeg" }));
        const up = await api.post("/admin/upload", fd);
        await api.put(`/admin/posters/${saved.data.id}`, { ...form, image_url: `${process.env.REACT_APP_BACKEND_URL}${up.data.url}` });
      } catch {
        toast.info("L'instantané n'a pas pu être figé — l'affiche reste visible en version dynamique.");
      }
      toast.success(editingId ? "Affiche mise à jour" : "Affiche publiée");
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({ ...EMPTY, ...item, secondary_images: item.secondary_images || [], logos: item.logos || [] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette affiche ?")) return;
    await api.delete(`/admin/posters/${id}`).catch(() => {});
    toast.success("Affiche supprimée");
    load();
  };

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      const name = safeName(form);
      if (kind === "pdf") await exportPosterPdf(form, `${name}.pdf`);
      else await exportPosterPng(form, `${name}.png`);
      toast.success(kind === "pdf" ? "PDF haute qualité téléchargé" : "Image PNG haute résolution téléchargée");
    } catch {
      toast.error("L'export a échoué. Réessayez.");
    } finally {
      setExporting("");
    }
  };

  return (
    <div data-testid="posters-manager" className="grid grid-cols-1 gap-10 xl:grid-cols-2">
      <form onSubmit={submit} className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">
          {editingId ? "Modifier l'affiche" : "Créer une affiche de concert"}
        </h2>
        <p className="text-xs text-stone-500">
          Remplissez les champs : l'affiche se construit en direct dans la prévisualisation. Format vertical A4/A3, prêt pour l'impression.
        </p>

        <div>
          <label className={labelClass}>Titre principal (ex : nom du groupe)</label>
          <input data-testid="poster-title-input" className={inputClass} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Les Sonneurs de la Côte" />
        </div>
        <div>
          <label className={labelClass}>Ligne secondaire du titre (même cartouche, facultatif)</label>
          <input data-testid="poster-title2-input" className={inputClass} value={form.title_2} onChange={(e) => set("title_2", e.target.value)} placeholder="ex : avec le Duo Trad'Anjou" />
        </div>
        <div>
          <label className={labelClass}>Sous-titre / nom de l'événement</label>
          <input data-testid="poster-subtitle-input" className={inputClass} value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Grand Bal Folk" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Date</label>
            <input data-testid="poster-date-input" type="date" className={inputClass} value={form.date} onChange={(e) => set("date", e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Heure</label>
            <input data-testid="poster-time-input" className={inputClass} value={form.time} onChange={(e) => set("time", e.target.value)} placeholder="19:00" />
          </div>
        </div>
        <div>
          <label className={labelClass}>Informations complémentaires</label>
          <textarea data-testid="poster-info-input" rows="2" className={inputClass} value={form.info} onChange={(e) => set("info", e.target.value)} placeholder={"17:30 Initiation aux danses folk"} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Lieu (nom de la salle)</label>
            <input data-testid="poster-venue-input" className={inputClass} value={form.venue_name} onChange={(e) => set("venue_name", e.target.value)} placeholder="Salle des fêtes" />
          </div>
          <div>
            <label className={labelClass}>Adresse (rue, code postal, ville)</label>
            <textarea data-testid="poster-address-input" rows="2" className={inputClass} value={form.venue_address} onChange={(e) => set("venue_address", e.target.value)} placeholder={"Rue du stade\n62730 Marck"} />
          </div>
        </div>
        <div>
          <label className={labelClass}>Tarifs (une ligne par tarif)</label>
          <textarea data-testid="poster-prices-input" rows="2" className={inputClass} value={form.prices} onChange={(e) => set("prices", e.target.value)} placeholder={"Tarif adulte : 10€\nTarif -18 ans : 5€"} />
        </div>
        <div>
          <label className={labelClass}>Contacts &amp; réservations</label>
          <textarea data-testid="poster-contact-input" rows="2" className={inputClass} value={form.contact} onChange={(e) => set("contact", e.target.value)} placeholder={"06 00 00 00 00\ncontact@sonneurs.fr"} />
        </div>
        <div>
          <label className={labelClass}>Lien du QR code (billetterie, site web...)</label>
          <input data-testid="poster-qr-input" className={inputClass} value={form.qr_url} onChange={(e) => set("qr_url", e.target.value)} placeholder="https://... (vide = pas de QR code)" />
        </div>

        <div className="rounded-lg border border-stone-200 p-4">
          <label className={labelClass}>Photo de fond principale (portrait, haute résolution)</label>
          <div className="flex flex-wrap items-center gap-3">
            <FileUploadButton testId="poster-main-image-upload" accept="image/*" label="Téléverser la photo" onUploaded={(url) => set("main_image", url)} />
            <button type="button" onClick={() => setPicker("main")} data-testid="poster-main-image-pick" className={pickerBtnClass}>
              <Images className="h-3.5 w-3.5" aria-hidden="true" /> Images du site
            </button>
            {form.main_image && (
              <button type="button" onClick={() => set("main_image", "")} data-testid="poster-main-image-remove" className="rounded border border-stone-300 p-2 text-stone-500 hover:border-stone-500" aria-label="Retirer">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="mt-2">
            <ImageLinkInput testId="poster-main-image-link" onAdd={(url) => set("main_image", url)} />
          </div>
          {form.main_image && <img src={toImageUrl(form.main_image)} alt="" className="mt-2 h-24 rounded object-cover" />}
        </div>

        <div className="rounded-lg border border-stone-200 p-4">
          <label className={labelClass}>Photos secondaires (1 ou 2, facultatif)</label>
          <div className="space-y-2">
            {form.secondary_images.map((url, i) => (
              <div key={i} className="flex items-center gap-3">
                <img src={toImageUrl(url)} alt="" className="h-14 w-20 rounded object-cover" />
                <button type="button" onClick={() => removeListItem("secondary_images", i)} data-testid={`poster-secondary-remove-${i}`} className="rounded border border-stone-300 p-2 text-stone-500 hover:border-stone-500" aria-label="Retirer">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          {form.secondary_images.length < 2 && (
            <div className="mt-2 space-y-2">
              <div className="flex flex-wrap gap-3">
                <FileUploadButton testId="poster-secondary-upload" accept="image/*" label="Ajouter une photo secondaire" onUploaded={(url) => addListItem("secondary_images", url)} />
                <button type="button" onClick={() => setPicker("secondary")} data-testid="poster-secondary-pick" className={pickerBtnClass}>
                  <Images className="h-3.5 w-3.5" aria-hidden="true" /> Images du site
                </button>
              </div>
              <ImageLinkInput testId="poster-secondary-link" onAdd={(url) => addListItem("secondary_images", url)} />
            </div>
          )}
        </div>

        <div className="rounded-lg border border-stone-200 p-4">
          <label className={labelClass}>Logos des sponsors / partenaires (bas de l'affiche)</label>
          <div className="flex flex-wrap gap-3">
            {form.logos.map((url, i) => (
              <div key={i} className="flex items-center gap-2 rounded border border-stone-200 p-1.5">
                <img src={toImageUrl(url)} alt="" className="h-10 object-contain" />
                <button type="button" onClick={() => removeListItem("logos", i)} data-testid={`poster-logo-remove-${i}`} className="rounded border border-stone-300 p-1.5 text-stone-500 hover:border-stone-500" aria-label="Retirer">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-2 space-y-2">
            <div className="flex flex-wrap gap-3">
              <FileUploadButton testId="poster-logo-upload" accept="image/*" label="Ajouter un logo" onUploaded={(url) => addListItem("logos", url)} />
              <button type="button" onClick={() => setPicker("logo")} data-testid="poster-logo-pick" className={pickerBtnClass}>
                <Images className="h-3.5 w-3.5" aria-hidden="true" /> Logos déjà ajoutés
              </button>
            </div>
            <ImageLinkInput testId="poster-logo-link" onAdd={(url) => addListItem("logos", url)} />
            <label className="flex items-center gap-2 text-xs text-stone-600">
              <input data-testid="poster-detour-checkbox" type="checkbox" checked={form.detour_logos} onChange={(e) => set("detour_logos", e.target.checked)} className="h-4 w-4 accent-[#B83227]" />
              Détourer les logos (supprimer le fond blanc — uniquement si le résultat est net)
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 rounded-lg border border-stone-200 p-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Style de l'affiche</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => set("template", "clair")} data-testid="poster-template-clair" className={`flex-1 rounded-lg border px-3 py-2 text-xs font-bold uppercase transition-colors duration-300 ${form.template !== "sombre" ? "border-[#B83227] bg-[#B83227] text-white" : "border-stone-300 text-stone-600 hover:border-stone-500"}`}>
                Clair
              </button>
              <button type="button" onClick={() => set("template", "sombre")} data-testid="poster-template-sombre" className={`flex-1 rounded-lg border px-3 py-2 text-xs font-bold uppercase transition-colors duration-300 ${form.template === "sombre" ? "border-[#B83227] bg-[#1E1B18] text-white" : "border-stone-300 text-stone-600 hover:border-stone-500"}`}>
                Sombre · bal du soir
              </button>
            </div>
          </div>
          <div>
            <label className={labelClass}>Taille du titre : {form.title_size}%</label>
            <input data-testid="poster-title-size" type="range" min="60" max="140" step="5" value={form.title_size} onChange={(e) => set("title_size", Number(e.target.value))} className="mt-3 w-full accent-[#B83227]" />
          </div>
        </div>

        <div className="rounded-lg border border-stone-200 p-4">
          <label className={labelClass}>Tailles d'écriture (%)</label>
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {[
              ["title2_size", "Ligne secondaire du titre", "poster-title2-size"],
              ["subtitle_size", "Sous-titre / événement", "poster-subtitle-size"],
              ["date_size", "Bloc date", "poster-date-size"],
              ["info_size", "Informations complémentaires", "poster-info-size"],
              ["panel_size", "Tarifs et lieu", "poster-panel-size"],
              ["contact_size", "Contact (pied de page)", "poster-contact-size"],
            ].map(([key, label, testId]) => (
              <div key={key}>
                <div className="flex justify-between text-xs text-stone-600">
                  <span>{label}</span>
                  <span className="font-bold">{form[key]}%</span>
                </div>
                <input data-testid={testId} type="range" min="60" max="140" step="5" value={form[key]} onChange={(e) => set(key, Number(e.target.value))} className="w-full accent-[#B83227]" />
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 rounded-lg border border-stone-200 p-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Couleur du thème</label>
            <div className="flex items-center gap-3">
              <input data-testid="poster-color-input" type="color" value={form.theme_color} onChange={(e) => set("theme_color", e.target.value)} className="h-10 w-16 cursor-pointer rounded border border-stone-300" />
              <span className="text-xs text-stone-500">{form.theme_color}</span>
            </div>
          </div>
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-stone-700">
            <input data-testid="poster-dark-text-checkbox" type="checkbox" checked={form.dark_text} onChange={(e) => set("dark_text", e.target.checked)} className="h-4 w-4 accent-[#B83227]" />
            Texte sombre (au lieu de clair)
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input data-testid="poster-published-checkbox" type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="h-4 w-4 accent-[#B83227]" />
          Visible sur la page Actualités
        </label>

        <div className="flex gap-3">
          <button type="submit" data-testid="poster-save-button" disabled={saving} className="rounded bg-[#B83227] px-6 py-2.5 text-xs font-bold uppercase text-white hover:bg-[#96281E] disabled:opacity-60">
            {saving ? "Enregistrement..." : editingId ? "Mettre à jour" : "Publier l'affiche"}
          </button>
          {editingId && (
            <button type="button" data-testid="poster-cancel-button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded border border-stone-300 px-6 py-2.5 text-xs font-bold uppercase text-stone-600">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="space-y-6">
        <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
          <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">Prévisualisation en direct</h2>
          <div className="mx-auto mt-4 max-w-[420px] overflow-hidden rounded-lg border border-stone-200" data-testid="poster-preview">
            <ScaledPoster poster={form} />
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button type="button" onClick={() => doExport("pdf")} disabled={!!exporting} data-testid="poster-export-pdf" className="inline-flex items-center gap-1.5 rounded bg-[#1E1B18] px-5 py-2.5 text-xs font-bold uppercase text-white hover:bg-stone-700 disabled:opacity-60">
              <FileDown className="h-3.5 w-3.5" /> {exporting === "pdf" ? "Export..." : "Télécharger en PDF (impression)"}
            </button>
            <button type="button" onClick={() => doExport("png")} disabled={!!exporting} data-testid="poster-export-png" className="inline-flex items-center gap-1.5 rounded border border-stone-400 px-5 py-2.5 text-xs font-bold uppercase text-stone-700 hover:border-stone-600 disabled:opacity-60">
              <ImageDown className="h-3.5 w-3.5" /> {exporting === "png" ? "Export..." : "Télécharger en PNG (HD)"}
            </button>
          </div>
          <p className="mt-2 text-xs text-stone-500">Le PDF est au format A4 haute définition, prêt à imprimer. Le PNG est idéal pour les réseaux sociaux.</p>
        </div>

        <div className="space-y-4">
          <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">Affiches enregistrées ({items.length})</h2>
          {items.length === 0 && <p className="text-sm text-stone-500">Aucune affiche pour le moment.</p>}
          {items.map((p) => (
            <div key={p.id} data-testid={`poster-item-${p.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                {p.image_url && <img src={p.image_url} alt="" className="h-16 rounded border border-stone-200" loading="lazy" />}
                <div>
                  <p className="text-sm font-bold text-[#1E1B18]">{p.subtitle || p.title || "Affiche sans titre"}</p>
                  <p className="text-xs text-stone-500">{p.date || "sans date"} · {p.published ? "Visible" : "Masquée"}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => edit(p)} data-testid={`poster-edit-${p.id}`} className="rounded border border-stone-300 p-2 text-stone-600 hover:border-stone-500" aria-label="Modifier">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => remove(p.id)} data-testid={`poster-delete-${p.id}`} className="rounded bg-red-50 p-2 text-red-700 hover:bg-red-100" aria-label="Supprimer">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {picker && (
        <ImagePicker
          onPick={(url) => {
            if (picker === "main") set("main_image", url);
            else if (picker === "secondary") addListItem("secondary_images", url);
            else addListItem("logos", url);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}
